import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Bot, CheckCircle2, CircleDashed, Clock3, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { availabilityRequestsApi } from "../../services/availabilityRequestsApi";
import { logsApi } from "../../services/logsApi";
import { ErrorState, LoadingState } from "../../components/States";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime } from "../../utils/format";
import { OPERATOR_AUTOMATION_BASE } from "../../app/moduleRegistry";

export function DashboardPage() {
  const requests = useQuery({ queryKey: ["requests"], queryFn: availabilityRequestsApi.list });
  const logs = useQuery({ queryKey: ["logs"], queryFn: logsApi.list });
  if (requests.isLoading || logs.isLoading) return <LoadingState label="Koostetaan dashboardia…" />;
  if (requests.isError || logs.isError) return <ErrorState />;

  const jobs = requests.data!.flatMap((request) =>
    request.jobs.map((job) => ({ ...job, request }))
  );
  const stats = [
    { label: "Avoimet kyselyt", value: requests.data!.filter((r) => r.overallStatus === "Avoin").length, icon: CircleDashed, tone: "blue" },
    { label: "Odottaa lähetystä", value: jobs.filter((j) => j.status === "Odottaa lähetystä").length, icon: Clock3, tone: "amber" },
    { label: "Kysytty Elisalta", value: jobs.filter((j) => j.status === "Kysytty Elisalta").length, icon: Bot, tone: "blue" },
    { label: "Vastaukset saatu", value: jobs.filter((j) => j.result).length, icon: CheckCircle2, tone: "green" },
    { label: "Vaatii huomiota", value: jobs.filter((j) => j.status.includes("Virhe") || j.status.includes("manuaalisen")).length, icon: TriangleAlert, tone: "red" }
  ];
  const latestJobs = jobs.sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5);
  const issues = logs.data!.filter((log) => log.severity !== "info").slice(0, 4);

  return (
    <>
      <section className="page-heading page-heading--hero">
        <div><span className="kicker">TILANNEKUVA</span><h1>Saatavuuskyselyt</h1><p>Elisa-saatavuuskyselyiden paikallinen työjono ja tuoreimmat tapahtumat.</p></div>
        <Link className="button button--primary" to={`${OPERATOR_AUTOMATION_BASE}/new`}>Uusi saatavuuskysely <ArrowRight size={17} /></Link>
      </section>

      <div className="stats-grid">
        {stats.map(({ label, value, icon: Icon, tone }) => (
          <article className="stat-card" key={label}>
            <span className={`stat-card__icon stat-card__icon--${tone}`}><Icon /></span>
            <div><span>{label}</span><strong>{value}</strong></div>
          </article>
        ))}
      </div>

      <div className="dashboard-grid">
        <section className="card card--span2">
          <div className="card__header"><div><h2>Viimeisimmät kyselytyöt</h2><p>Ajantasainen näkymä paikalliseen työjonoon</p></div><Link className="text-link" to={`${OPERATOR_AUTOMATION_BASE}/open`}>Näytä kaikki <ArrowRight size={15} /></Link></div>
          <div className="table-wrap">
            <table><thead><tr><th>Tunnus</th><th>Kohde</th><th>Nopeus</th><th>Tila</th><th>Luotu</th></tr></thead>
              <tbody>{latestJobs.map((job) => <tr key={job.id}>
                <td><strong>{job.request.requestCode}</strong></td><td>{job.request.streetAddress}, {job.request.city}</td>
                <td><span className="speed-chip">{job.speed}</span></td><td><StatusBadge status={job.status} /></td><td>{formatDateTime(job.createdAt)}</td>
              </tr>)}</tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <div className="card__header"><div><h2>Prosessin kulku</h2><p>Elisa-saatavuuskyselyn automatisoitu elinkaari</p></div></div>
          <ol className="process">
            {[
              {
                title: "Saatavuuskysely luodaan",
                description: "Kohde- ja liittymätiedot tallennetaan sovellukseen."
              },
              {
                title: "Kysely lähetetään Elisalle",
                description: "Robotti tekee kyselyn ja tallentaa Elisan tapahtumatunnisteen."
              },
              {
                title: "Tilanne tarkistetaan kolmesti päivässä",
                description: "Ajastettu robotti seuraa avoimia kyselyitä tapahtumatunnisteen perusteella."
              },
              {
                title: "Vastaus käsitellään ja toimitetaan",
                description: "Hyväksytyn vastauksen hinnat ja lisätiedot tai hylkäystieto tallennetaan ja lähetetään operaattorimyyntiin."
              }
            ].map((item, i) =>
              <li key={item.title}><span>{i + 1}</span><div><strong>{item.title}</strong><small>{item.description}</small></div></li>
            )}
          </ol>
        </section>

        <section className="card card--span2">
          <div className="card__header"><div><h2>Viimeisimmät huomiot</h2><p>Varoitukset ja virhetilanteet</p></div><Link className="text-link" to={`${OPERATOR_AUTOMATION_BASE}/logs`}>Avaa lokit <ArrowRight size={15} /></Link></div>
          {issues.length ? <div className="event-list">{issues.map((log) =>
            <div className="event-row" key={log.id}><span className={`severity-dot severity-dot--${log.severity}`} /><div><strong>{log.message}</strong><span>{log.requestCode} · {log.speed ?? "yleinen"}</span></div><time>{formatDateTime(log.timestamp)}</time></div>
          )}</div> : <p className="muted">Ei avoimia huomioita.</p>}
        </section>
      </div>
    </>
  );
}
